import { Controller } from "stimulus";

export default class extends Controller {
  connect() {
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.element.addEventListener("click", this.onClick);

    this.zoomCursorImages = [];
    this.element.querySelectorAll("figure.attachment--preview img").forEach((img) => {
      const figure = img.closest("figure.attachment--preview");
      if (figure && this.isVideoFigure(figure)) return;
      img.style.cursor = "zoom-in";
      this.zoomCursorImages.push(img);
    });
  }

  isVideoFigure(figure) {
    if (figure.querySelector("video, .bp-video-blob-wrapper")) return true;
    const contentType = figure.getAttribute("data-trix-content-type") || "";
    return contentType.startsWith("video/");
  }

  disconnect() {
    this.close();
    this.element.removeEventListener("click", this.onClick);
    if (this.zoomCursorImages) {
      this.zoomCursorImages.forEach((img) => {
        if (img.style.cursor === "zoom-in") img.style.cursor = "";
      });
      this.zoomCursorImages = null;
    }
    this.onClick = null;
    this.onKeydown = null;
  }

  onClick(event) {
    if (event.target.closest(".bp-video-blob-wrapper, video")) return;

    const figure = event.target.closest("figure.attachment--preview");
    if (!figure) return;
    if (this.isVideoFigure(figure)) return;
    const img = figure.querySelector("img");
    if (!img || !img.src) return;
    event.preventDefault();

    const images = Array.from(this.element.querySelectorAll("figure.attachment--preview img"))
      .filter((candidate) => {
        const candidateFigure = candidate.closest("figure.attachment--preview");
        return candidateFigure && !this.isVideoFigure(candidateFigure);
      });
    const index = images.indexOf(img);
    this.open(images, index === -1 ? 0 : index);
  }

  onKeydown(event) {
    if (!this.overlay) return;
    if (event.key === "Escape") {
      this.close();
    } else if (event.key === "ArrowLeft") {
      this.show(this.currentIndex - 1);
    } else if (event.key === "ArrowRight") {
      this.show(this.currentIndex + 1);
    }
  }

  open(images, startIndex) {
    this.close();

    this.images = images;
    this.currentIndex = startIndex;

    const overlay = document.createElement("div");
    overlay.className = "bp-image-lightbox";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");

    const closeButton = this.buildButton("bp-image-lightbox-close", "Close", "&times;");

    const downloadButton = this.buildButton(
      "bp-image-lightbox-download",
      "Download",
      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>'
    );
    downloadButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.download();
    });

    const prevButton = this.buildButton("bp-image-lightbox-prev", "Previous image", "&#8249;");
    prevButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.show(this.currentIndex - 1);
    });

    const nextButton = this.buildButton("bp-image-lightbox-next", "Next image", "&#8250;");
    nextButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.show(this.currentIndex + 1);
    });

    const image = document.createElement("img");
    image.className = "bp-image-lightbox-image";

    const counter = document.createElement("div");
    counter.className = "bp-image-lightbox-counter";

    const stage = document.createElement("div");
    stage.className = "bp-image-lightbox-stage";
    stage.appendChild(closeButton);
    stage.appendChild(downloadButton);
    stage.appendChild(prevButton);
    stage.appendChild(image);
    stage.appendChild(nextButton);
    stage.appendChild(counter);

    overlay.appendChild(stage);

    overlay.addEventListener("click", (event) => {
      if (event.target === overlay || event.target === closeButton) {
        this.close();
      }
    });

    document.body.appendChild(overlay);
    document.body.style.overflow = "hidden";

    this.overlay = overlay;
    this.imageElement = image;
    this.counterElement = counter;
    this.prevButton = prevButton;
    this.nextButton = nextButton;

    document.addEventListener("keydown", this.onKeydown);
    this.render();
  }

  buildButton(className, label, glyph) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = className;
    button.setAttribute("aria-label", label);
    button.innerHTML = glyph;
    return button;
  }

  show(index) {
    if (!this.images || this.images.length === 0) return;
    const total = this.images.length;
    this.currentIndex = ((index % total) + total) % total;
    this.render();
  }

  render() {
    const img = this.images[this.currentIndex];
    if (!img) return;
    this.imageElement.src = img.src;
    this.imageElement.alt = img.alt || "";

    const multiple = this.images.length > 1;
    this.prevButton.style.display = multiple ? "" : "none";
    this.nextButton.style.display = multiple ? "" : "none";
    this.counterElement.style.display = multiple ? "" : "none";
    this.counterElement.textContent = `${this.currentIndex + 1} / ${this.images.length}`;
  }

  async download() {
    const img = this.images && this.images[this.currentIndex];
    if (!img || !img.src) return;
    const src = img.src;
    let filename = "image";
    try {
      const url = new URL(src, window.location.href);
      const last = url.pathname.split("/").filter(Boolean).pop();
      if (last) filename = decodeURIComponent(last);
    } catch (_e) {}

    try {
      const response = await fetch(src, { credentials: "omit" });
      if (!response.ok) throw new Error("fetch failed");
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (_e) {
      window.open(src, "_blank", "noopener");
    }
  }

  close() {
    if (!this.overlay) return;

    this.overlay.remove();
    document.body.style.overflow = "";
    if (this.onKeydown) document.removeEventListener("keydown", this.onKeydown);

    this.overlay = null;
    this.images = null;
    this.currentIndex = 0;
    this.imageElement = null;
    this.counterElement = null;
    this.prevButton = null;
    this.nextButton = null;
  }
}
